<template>
  <div class="cws-container">
    <b-row>
      <b-col cols="12">
        <b-card title="Собрать пак из файлов" border-variant="primary">
          <b-card-text>
            Загрузите ZIP-архив со структурой <code>[Обёртка/]N. Тема/Исполнитель - Трек.mp3</code>.
            MP3 длиннее 30 сек будут обрезаны, изображения уменьшены до 1100×900.
          </b-card-text>

          <b-form-group label="Архив (.zip)" label-for="archive-file">
            <b-form-file
                id="archive-file"
                v-model="archive"
                accept=".zip"
                placeholder="Выберите ZIP-архив..."
                browse-text="Обзор"
                :disabled="isProcessing"
            />
          </b-form-group>

          <b-row>
            <b-col md="2"><b-form-group label="Раунды"><b-form-select v-model.number="form.rounds" :options="roundsOptions" :disabled="isProcessing"/></b-form-group></b-col>
            <b-col md="2"><b-form-group label="Темы/раунд"><b-form-select v-model.number="form.topics" :options="topicsOptions" :disabled="isProcessing"/></b-form-group></b-col>
            <b-col md="2"><b-form-group label="Вопросы/тема"><b-form-select v-model.number="form.questions" :options="questionsOptions" :disabled="isProcessing"/></b-form-group></b-col>
            <b-col md="2"><b-form-group label="Обрезка: длина, сек"><b-form-input v-model.number="form.cut_duration" type="number" min="1" :disabled="isProcessing"/></b-form-group></b-col>
            <b-col md="2">
              <b-form-group label="Старт обрезки, сек">
                <b-form-input v-model.number="form.cut_start" type="number" min="0" :disabled="isProcessing || form.random_cut"/>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="4"><b-form-group label="Название пакета (необязательно)"><b-form-input v-model="form.pack_name" :disabled="isProcessing"/></b-form-group></b-col>
            <b-col md="8" class="d-flex align-items-center flex-wrap">
              <b-form-checkbox v-model="form.random_order" :disabled="isProcessing" class="mr-2">Случайный порядок вопросов</b-form-checkbox>
              <b-form-checkbox v-model="form.random_cut" :disabled="isProcessing" class="mr-2">Случайный старт обрезки</b-form-checkbox>
              <b-form-checkbox v-model="form.abbr" :disabled="isProcessing">Подсказки-аббревиатуры</b-form-checkbox>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="8" class="d-flex align-items-center flex-wrap">
              <b-form-checkbox v-model="form.flat_rounds" :disabled="isProcessing" class="mr-2">Плоские раунды</b-form-checkbox>
              <b-form-checkbox v-model="form.flat_themes" :disabled="isProcessing">Плоские темы</b-form-checkbox>
            </b-col>
          </b-row>

          <b-button variant="primary" :disabled="!archive || isProcessing" @click="build">
            <b-spinner v-if="isProcessing" small class="mr-1"/>
            Собрать пакет
          </b-button>

          <div v-if="isProcessing || statusText" class="mt-3">
            <b-progress :max="progressMax" height="1.2rem">
              <b-progress-bar :value="progressValue" :variant="progressVariant">
                {{ progressLabel }}
              </b-progress-bar>
            </b-progress>
            <small class="text-muted">{{ statusText }}</small>
          </div>

          <b-button v-if="downloadInfo" variant="success" class="mt-3" @click="download">
            <feather-icon icon="DownloadIcon" class="mr-50"/>
            Скачать {{ downloadInfo.filename }}
          </b-button>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardText, BFormGroup, BFormFile, BFormInput,
  BFormSelect, BFormCheckbox, BButton, BSpinner, BProgress, BProgressBar,
} from 'bootstrap-vue'
import { ref, reactive, computed, onUnmounted } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import musicStoreModule from '@/views/db-music/musicStoreModule'

export default {
  components: {
    BRow, BCol, BCard, BCardText, BFormGroup, BFormFile, BFormInput,
    BFormSelect, BFormCheckbox, BButton, BSpinner, BProgress, BProgressBar,
  },
  setup() {
    const STORE_MODULE_NAME = 'playlist'
    const toast = useToast()
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const archive = ref(null)
    const isProcessing = ref(false)
    const statusText = ref('')
    const downloadInfo = ref(null)
    const progressValue = ref(0)
    const progressMax = ref(1)
    const progressVariant = ref('primary')

    const form = reactive({
      rounds: 3, topics: 6, questions: 5,
      random_order: true, random_cut: true, cut_start: 55, cut_duration: 25, pack_name: '', abbr: true,
      flat_rounds: false, flat_themes: false,
    })

    const range = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => from + i)
    const roundsOptions = range(1, 5)
    const topicsOptions = range(1, 12)
    const questionsOptions = range(1, 10)

    const progressLabel = computed(() => {
      if (progressMax.value <= 1) return statusText.value ? '...' : ''
      return `${progressValue.value} / ${progressMax.value}`
    })

    const onProgress = payload => {
      if (payload.event === 'extract') {
        statusText.value = `Распаковка: найдено файлов — ${payload.total}`
        progressMax.value = payload.total || 1
        progressValue.value = 0
      } else if (payload.event === 'preprocess') {
        progressMax.value = payload.total || 1
        progressValue.value = payload.done || 0
        statusText.value = `Обработка файлов: ${payload.file || ''}`
      } else if (payload.event === 'build') {
        statusText.value = 'Сборка структуры пакета...'
      } else if (payload.event === 'generate') {
        statusText.value = 'Генерация .siq...'
      } else if (payload.event === 'done') {
        progressValue.value = progressMax.value
        progressVariant.value = 'success'
        statusText.value = 'Готово'
      }
    }

    const build = async () => {
      if (!archive.value) return
      isProcessing.value = true
      downloadInfo.value = null
      statusText.value = 'Загрузка архива...'
      progressValue.value = 0
      progressMax.value = 1
      progressVariant.value = 'primary'

      try {
        const result = await store.dispatch('playlist/packFromFiles', {
          file: archive.value,
          options: {
            rounds: form.rounds,
            topics: form.topics,
            questions: form.questions,
            random_order: form.random_order ? 1 : 0,
            random_cut: form.random_cut ? 1 : 0,
            flat_rounds: form.flat_rounds ? 1 : 0,
            flat_themes: form.flat_themes ? 1 : 0,
            cut_start: form.cut_start,
            cut_duration: form.cut_duration,
            pack_name: form.pack_name,
            abbr: form.abbr ? 1 : 0,
          },
          onProgress,
        })
        downloadInfo.value = result
      } catch (error) {
        progressVariant.value = 'danger'
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сборки пакета',
            text: error.message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isProcessing.value = false
      }
    }

    const download = async () => {
      if (!downloadInfo.value) return
      try {
        await store.dispatch('playlist/downloadPackResult', {
          id: downloadInfo.value.download_id,
          filename: downloadInfo.value.filename,
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: { title: 'Ошибка скачивания', text: error.message, icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      }
    }

    return {
      archive, isProcessing, statusText, downloadInfo,
      progressValue, progressMax, progressVariant, progressLabel,
      form, build, download,
      roundsOptions, topicsOptions, questionsOptions,
    }
  },
}
</script>
